import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import BackButton from '../../components/BackButton';
import api from '../../services/api';
import './UserProfile.css';

const UserProfile = () => {
  const { username } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isMyProfile = !username || username === user?.username;
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    displayName: '',
    bio: '',
    avatarUrl: '',
    profileVisible: true
  });

  useEffect(() => {
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username]);

  const loadProfile = async () => {
    try {
      const endpoint = isMyProfile ? '/api/chat/profile/me' : `/api/chat/profile/${username}`;
      const response = await api.get(endpoint);
      setProfile(response.data);
      if (isMyProfile) {
        setFormData({
          displayName: response.data.displayName || '',
          bio: response.data.bio || '',
          avatarUrl: response.data.avatarUrl || '',
          profileVisible: response.data.profileVisible !== false
        });
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/chat/profile/create', formData);
      setEditing(false);
      loadProfile();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to save profile');
    }
  };

  const toggleVisibility = async () => {
    try {
      await api.put('/api/chat/profile/toggle-visibility');
      loadProfile();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to toggle visibility');
    }
  };

  const startChat = () => {
    if (username && username !== user?.username) {
      navigate(`/chat/direct/${username}`);
    }
  };

  if (loading) {
    return (
      <div className="user-profile">
        <BackButton />
        <div className="user-profile-loading">Loading profile...</div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="user-profile">
        <BackButton />
        <div className="user-profile-error">Profile not found</div>
      </div>
    );
  }

  return (
    <div className="user-profile">
      <BackButton />
      <div className="profile-container">
        {isMyProfile && editing ? (
          <form onSubmit={saveProfile} className="profile-form">
            <h2>Edit Profile</h2>
            <div className="form-group">
              <label>Display Name</label>
              <input
                type="text"
                value={formData.displayName}
                onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                placeholder="Display Name"
              />
            </div>
            <div className="form-group">
              <label>Bio</label>
              <textarea
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                placeholder="Tell us about yourself"
                rows={4}
              />
            </div>
            <div className="form-group">
              <label>Avatar URL</label>
              <input
                type="url"
                value={formData.avatarUrl}
                onChange={(e) => setFormData({ ...formData, avatarUrl: e.target.value })}
                placeholder="https://example.com/avatar.jpg"
              />
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.profileVisible}
                  onChange={(e) => setFormData({ ...formData, profileVisible: e.target.checked })}
                />
                Make profile visible to others
              </label>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn-save">Save</button>
              <button type="button" onClick={() => setEditing(false)} className="btn-cancel">Cancel</button>
            </div>
          </form>
        ) : (
          <div className="profile-view">
            <div className="profile-header">
              <div className="profile-avatar">
                {profile.avatarUrl ? (
                  <img src={profile.avatarUrl} alt={profile.displayName || profile.username} />
                ) : (
                  <div className="avatar-placeholder-large">
                    {profile.displayName?.charAt(0) || profile.username.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <div className="profile-info">
                <h1>{profile.displayName || profile.username}</h1>
                <p className="username">@{profile.username}</p>
                {profile.isPremium && <span className="badge-premium">⭐ Premium Member</span>}
                {profile.profileVisible ? (
                  <span className="badge-visible">👁️ Profile Visible</span>
                ) : (
                  <span className="badge-hidden">🔒 Profile Hidden</span>
                )}
              </div>
            </div>

            {profile.bio && (
              <div className="profile-bio">
                <h3>About</h3>
                <p>{profile.bio}</p>
              </div>
            )}

            {profile.lastSeen && (
              <div className="profile-meta">
                <p>Last seen: {new Date(profile.lastSeen).toLocaleString()}</p>
              </div>
            )}

            <div className="profile-actions">
              {isMyProfile ? (
                <>
                  <button onClick={() => setEditing(true)} className="btn-edit">Edit Profile</button>
                  <button onClick={toggleVisibility} className="btn-toggle">
                    {profile.profileVisible ? 'Hide Profile' : 'Show Profile'}
                  </button>
                </>
              ) : (
                <button onClick={startChat} className="btn-chat">Start Chat</button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfile;

